import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Card, Col, Modal, Row, Typography, message,
} from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import {
  Highlighteable,
  TruckIcon,
  VehicleType,
} from '../../components';
import { createDefaultMap, createPinMarker, zoomTo } from '../../services/map/drawing';

const getLicensePlate = (asset) => asset?.asset?.licensePlate;

const getAssetType = (asset) => {
  const assetType = asset?.asset?.type;
  return (!assetType || assetType === 'N/A') ? null : assetType;
};

const getCoordinates = (asset) => asset?.asset?.lastTrace?.coordinates;

const hasCoordinates = (coordinates) => (
  coordinates?.latitude !== undefined && coordinates?.longitude !== undefined
);

const formatCoordinates = (coordinates, precision = 5) => {
  if (!hasCoordinates(coordinates)) {
    return null;
  }

  const latitude = +parseFloat(coordinates.latitude).toFixed(precision);
  const longitude = +parseFloat(coordinates.longitude).toFixed(precision);
  return `${latitude}, ${longitude}`;
};

const formatCoordinatesForCopy = (coordinates) => {
  if (!hasCoordinates(coordinates)) {
    return null;
  }

  return `${+parseFloat(coordinates.latitude)},${+parseFloat(coordinates.longitude)}`;
};

const SummaryCard = styled(Card)`
  .ant-card-body {
    padding: 10px 12px;
  }
`;

const SummaryCardContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SummaryCardText = styled.div`
  min-width: 0;
`;

const SummaryTitle = styled(Typography.Text)`
  display: block;
  color: ${({ theme: currentTheme }) => currentTheme.color.darkgray1};
  font-size: 12px;
`;

const SummaryValue = styled(Typography.Text)`
  display: block;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.2;
  color: ${({ theme: currentTheme }) => currentTheme.color.primary};
`;

const ModalLicensePlate = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme: currentTheme }) => currentTheme.color.primary};
  font-size: 16px;
`;

const ModalLicensePlateDetails = styled.div`
  display: inline-flex;
  flex-direction: column;
  gap: 4px;
`;

const ModalLicensePlateValue = styled.span`
  line-height: 1.2;
`;

const ModalAssetType = styled.span`
  color: darkgray;
  line-height: 1.2;
`;

const TruckIconBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border-radius: 6px;
  background: ${({ theme: currentTheme }) => currentTheme.color.backgroundLight3};
`;

const MiniMapContainer = styled.div`
  width: 100%;
  height: clamp(260px, 36vh, 360px);
  border-radius: 8px;
  overflow: hidden;
  background: #f5f5f5;
`;

const CoordinatesCopyButton = styled.button`
  width: 100%;
  border: none;
  background: transparent;
  padding: 0;
  text-align: left;
  cursor: pointer;
`;

const CoordinatesValue = styled(SummaryValue)`
  font-size: 16px;
  word-break: break-word;
`;

const CoordinatesCopyIcon = styled.span`
  font-size: 16px;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
`;

const CoordinatesCard = styled(SummaryCard)`
  background: ${({ theme: currentTheme }) => currentTheme.color.backgroundLight3};
`;

const CoordinatesCardContent = styled(SummaryCardContent)`
  position: relative;
`;

const CoordinatesCardCopyIcon = styled(CoordinatesCopyIcon)`
  position: absolute;
  right: 0;
  bottom: 0;
`;

const MAP_ID = 'locations-license-plate-map';

const AssetDetailsModal = ({ open, onCancel, asset }) => {
  const { t } = useTranslation();
  const [mapContainerNode, setMapContainerNode] = useState(null);
  const mapUiRef = useRef(null);

  const selectedLicensePlate = getLicensePlate(asset);
  const selectedAssetType = getAssetType(asset);
  const selectedCoordinates = getCoordinates(asset);
  const hasSelectedCoordinates = hasCoordinates(selectedCoordinates);

  const copyText = async (text) => {
    if (!text) {
      return false;
    }

    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }

    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(textArea);
    return copied;
  };

  const onCoordinatesCopy = async () => {
    try {
      const copied = await copyText(formatCoordinatesForCopy(selectedCoordinates));
      if (copied) {
        message.success('Coordinates copied');
      } else {
        message.error('Unable to copy coordinates');
      }
    } catch (copyError) {
      message.error('Unable to copy coordinates');
    }
  };

  useEffect(() => {
    if (!open || !asset || !hasSelectedCoordinates || !mapContainerNode) {
      return undefined;
    }

    const ui = createDefaultMap(MAP_ID);
    mapUiRef.current = ui;

    const marker = createPinMarker({
      coordinateLatitude: selectedCoordinates.latitude,
      coordinateLongitude: selectedCoordinates.longitude,
      ignitionState: asset?.asset?.lastTrace?.ignitionState,
    });

    if (marker) {
      ui.getMap().addObject(marker);
    }

    zoomTo(
      ui,
      { lat: selectedCoordinates.latitude, lng: selectedCoordinates.longitude },
      14,
      false,
    );
    ui.getMap().getViewPort().resize();

    return () => {
      if (mapUiRef.current) {
        mapUiRef.current.getMap().dispose();
        mapUiRef.current = null;
      }
    };
  }, [
    open,
    asset,
    hasSelectedCoordinates,
    mapContainerNode,
    selectedCoordinates?.latitude,
    selectedCoordinates?.longitude,
  ]);

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      width={700}
      destroyOnClose
    >
      <div style={{ marginBottom: '16px' }}>
        <ModalLicensePlate>
          <TruckIconBadge>
            <TruckIcon style={{ color: '#4ADE80' }} />
          </TruckIconBadge>
          <ModalLicensePlateDetails>
            <ModalLicensePlateValue>
              <Highlighteable
                content={selectedLicensePlate || t('COMMON.MISSING')}
                search={selectedLicensePlate || t('COMMON.MISSING')}
              />
            </ModalLicensePlateValue>
            <ModalAssetType>
              {selectedAssetType
                ? <VehicleType type={selectedAssetType} />
                : t('LOCATIONS.UNKNOWN_ASSET_TYPE')}
            </ModalAssetType>
          </ModalLicensePlateDetails>
        </ModalLicensePlate>
      </div>
      <Row gutter={[12, 12]}>
        <Col span={24}>
          {hasSelectedCoordinates ? (
            <CoordinatesCopyButton
              type="button"
              onClick={onCoordinatesCopy}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onCoordinatesCopy();
                }
              }}
            >
              <CoordinatesCard size="small">
                <CoordinatesCardContent>
                  <SummaryCardText>
                    <SummaryTitle>{t('PROPERTIES.COORDINATES')}</SummaryTitle>
                    <CoordinatesValue>{formatCoordinates(selectedCoordinates)}</CoordinatesValue>
                  </SummaryCardText>
                  <CoordinatesCardCopyIcon>
                    <CopyOutlined />
                  </CoordinatesCardCopyIcon>
                </CoordinatesCardContent>
              </CoordinatesCard>
            </CoordinatesCopyButton>
          ) : (
            <Card size="small" title={t('PROPERTIES.COORDINATES')}>
              {t('COMMON.MISSING')}
            </Card>
          )}
        </Col>
        <Col span={24}>
          <Card size="small">
            {hasSelectedCoordinates ? (
              <MiniMapContainer id={MAP_ID} ref={setMapContainerNode} />
            ) : t('COMMON.MISSING')}
          </Card>
        </Col>
      </Row>
    </Modal>
  );
};

AssetDetailsModal.propTypes = {
  open: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  asset: PropTypes.shape({
    asset: PropTypes.shape({
      licensePlate: PropTypes.string,
      type: PropTypes.string,
      lastTrace: PropTypes.shape({
        ignitionState: PropTypes.string,
        coordinates: PropTypes.shape({
          latitude: PropTypes.number,
          longitude: PropTypes.number,
        }),
      }),
    }),
  }),
};

AssetDetailsModal.defaultProps = {
  open: false,
  asset: null,
};

export default AssetDetailsModal;
